import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@/components/Icons';

type CalendarDatePickerProps = {
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minDate?: string;
  maxDate?: string;
  align?: 'start' | 'end';
};

const MONTH_LABEL_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
});

const TRIGGER_LABEL_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

function toLocalDate(value: string) {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split('-').map((part) => Number(part));

  if (!year || !month || !day) {
    return null;
  }

  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
}

function formatLocalDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addDays(date: Date, amount: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function startOfWeekMonday(date: Date) {
  const next = new Date(date);
  const day = next.getDay();
  const offset = (day + 6) % 7;
  next.setDate(next.getDate() - offset);
  next.setHours(0, 0, 0, 0);
  return next;
}

function isSameDay(left: Date, right: Date) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function isBeforeDay(left: Date, right: Date) {
  return left.getTime() < right.getTime();
}

function isAfterDay(left: Date, right: Date) {
  return left.getTime() > right.getTime();
}

function buildMonthGrid(viewMonth: Date) {
  const monthStart = startOfMonth(viewMonth);
  const gridStart = startOfWeekMonday(monthStart);

  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
}

export function CalendarDatePicker({
  ariaLabel,
  value,
  onChange,
  placeholder = 'Pick a date',
  minDate,
  maxDate,
  align = 'start',
}: CalendarDatePickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const selectedDate = useMemo(() => toLocalDate(value), [value]);
  const minDateValue = useMemo(() => toLocalDate(minDate ?? ''), [minDate]);
  const maxDateValue = useMemo(() => toLocalDate(maxDate ?? ''), [maxDate]);
  const initialViewMonth = useMemo(() => {
    return startOfMonth(selectedDate ?? minDateValue ?? maxDateValue ?? new Date());
  }, [maxDateValue, minDateValue, selectedDate]);
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(initialViewMonth);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    setViewMonth(initialViewMonth);
  }, [initialViewMonth, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const monthDays = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);
  const monthLabel = useMemo(() => MONTH_LABEL_FORMAT.format(viewMonth), [viewMonth]);
  const triggerLabel = selectedDate ? TRIGGER_LABEL_FORMAT.format(selectedDate) : placeholder;
  const canGoPrevious = !minDateValue || endOfMonth(addMonths(viewMonth, -1)).getTime() >= minDateValue.getTime();
  const canGoNext = !maxDateValue || startOfMonth(addMonths(viewMonth, 1)).getTime() <= maxDateValue.getTime();

  function selectDate(date: Date) {
    if (minDateValue && isBeforeDay(date, minDateValue)) {
      return;
    }

    if (maxDateValue && isAfterDay(date, maxDateValue)) {
      return;
    }

    onChange(formatLocalDate(date));
    setIsOpen(false);
  }

  return (
    <div ref={rootRef} className="calendar-picker">
      <button
        className={`calendar-picker-trigger${selectedDate ? '' : ' placeholder'}`}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((current) => !current)}>
        <span className="calendar-picker-trigger-value">{triggerLabel}</span>
        <CalendarIcon size={18} />
      </button>

      {isOpen ? (
        <div id={panelId} className={`calendar-picker-panel align-${align}`} role="dialog" aria-label={ariaLabel}>
          <div className="calendar-picker-header">
            <button
              className="calendar-picker-nav"
              type="button"
              onClick={() => setViewMonth((current) => addMonths(current, -1))}
              disabled={!canGoPrevious}
              aria-label="Previous month">
              <ChevronLeftIcon size={16} />
            </button>

            <div className="calendar-picker-header-copy">
              <strong className="calendar-picker-month">{monthLabel}</strong>
              <span className="calendar-picker-subtitle">{selectedDate ? 'Selected date' : 'Choose a day'}</span>
            </div>

            <button
              className="calendar-picker-nav"
              type="button"
              onClick={() => setViewMonth((current) => addMonths(current, 1))}
              disabled={!canGoNext}
              aria-label="Next month">
              <ChevronRightIcon size={16} />
            </button>
          </div>

          <div className="calendar-picker-grid-head" aria-hidden="true">
            {WEEKDAY_LABELS.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="calendar-picker-grid" role="grid" aria-label={`${ariaLabel} calendar`}>
            {monthDays.map((day) => {
              const outsideMonth = day.getMonth() !== viewMonth.getMonth();
              const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
              const isDisabled = Boolean(
                (minDateValue && isBeforeDay(day, minDateValue)) || (maxDateValue && isAfterDay(day, maxDateValue))
              );

              return (
                <button
                  key={day.toISOString()}
                  className={`calendar-picker-day${outsideMonth ? ' outside' : ''}${isSelected ? ' selected' : ''}`}
                  type="button"
                  onClick={() => selectDate(day)}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                  aria-label={TRIGGER_LABEL_FORMAT.format(day)}>
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <div className="calendar-picker-footer">
            <button
              className="calendar-picker-link"
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
              disabled={!value}>
              Clear
            </button>
            <span>{selectedDate ? TRIGGER_LABEL_FORMAT.format(selectedDate) : 'Use the calendar to choose a date'}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
